import React, { ReactNode } from "react";
import Image from "next/image";

// icons (SVG React components)
import IconVet from "@/shared/media/icons/icon-vet.svg";
import BadgePercent from "@/shared/media/icons/badgePercent.svg";
import ShieldDog from "@/shared/media/icons/shieldDog.svg";

// accolades logos
import Marketwatch from "@/shared/media/icons/market-watch.svg";
import Forbes from "@/shared/media/icons/forbes.svg";
import HelloSafe from "@/shared/media/icons/hellosafe.svg";
import { Badge } from "@/shared/components/ui/Badge";
import { cn } from "@/shared/utils";

// perks logos
import fiSrc from "@/shared/media/images/perks/fi.webp";
import fableSrc from "@/shared/media/images/perks/fable-logo.webp";
import whiskerSrc from "@/shared/media/images/perks/whisker-logo.webp";
import mavenSrc from "@/shared/media/images/perks/maven.webp";

export type CalloutBlockquote = {
    topText: ReactNode;
    bottomText: ReactNode;
};

export interface PrettyStepOneProps {
    title?: ReactNode;
    subTitle?: ReactNode;
    description?: ReactNode;
    descriptionClass?: string;
    blockquoteDescription?: string;
    blockquotes?: CalloutBlockquote[];
}

type WhySpotBlurbProps = {
    icon: ReactNode;
    heading: string;
    description: string;
    reference?: string;
    headingReference?: string;
};

const ICON_SIZES = "block size-6 sm:size-8 lg:size-6 xl:size-8";
const ACCOLADE_SIZES = "w-full sm:max-w-[192px] md:max-w-[249px] lg:max-w-[203px] xl:max-w-[285px]";
const PERK_IMAGE_BASE = "w-full h-auto aspect-[3/2] object-cover rounded-lg";

const BLURBS: WhySpotBlurbProps[] = [
    { icon: <IconVet className={ICON_SIZES} aria-hidden="true" />, heading: "Visit any licensed vet", description: "in Canada and the U.S." },
    { icon: <BadgePercent className={ICON_SIZES} aria-hidden="true" />, heading: "Save up to 10%", description: "Multi-pet policy discounts available", headingReference: "1" },
    { icon: <ShieldDog className={ICON_SIZES} aria-hidden="true" />, heading: "Next-day coverage", description: "for accident care" },
    { icon: <BadgePercent className={ICON_SIZES} aria-hidden="true" />, heading: "$1,500", description: "in special perks and discounts", headingReference: "2" }
];

type AccoladeEntry = { key: string; component: ReactNode };

const ACCOLADES: AccoladeEntry[] = [
    { key: "mw", component: <Marketwatch className={ACCOLADE_SIZES} aria-hidden="true" /> },
    { key: "fb", component: <Forbes className={ACCOLADE_SIZES} aria-hidden="true" /> },
    { key: "hs", component: <HelloSafe className={ACCOLADE_SIZES} aria-hidden="true" /> }
];

const PERKS = [
    { src: fiSrc, alt: "Fi" },
    { src: fableSrc, alt: "Fable" },
    { src: whiskerSrc, alt: "Whisker" },
    { src: mavenSrc, alt: "Maven" }
];

function WhySpotBlurb({ icon, heading, description, reference, headingReference }: WhySpotBlurbProps) {
    return (
        <div data-testid="whyspot-blurb" className="flex items-center gap-2 xs:gap-3 sm:items-start lg:items-center lg:gap-2 xl:items-start xl:gap-4">
            <div className="shrink-0">{icon}</div>
            <div>
                <h3 className="text-sm font-bold leading-5 text-content-primary sm:text-base lg:text-sm xl:text-base">
                    {heading}
                    {headingReference && <sup>{headingReference}</sup>}
                </h3>
                <p className="text-xs font-medium leading-4 text-content-secondary sm:text-sm sm:leading-5 md:text-base lg:text-xs lg:leading-4 xl:text-sm xl:leading-5">
                    {description}
                    {reference && <sup>{reference}</sup>}
                </p>
            </div>
        </div>
    );
}

export default function PrettyStepOne({ title, subTitle, description, blockquoteDescription, blockquotes }: PrettyStepOneProps) {
    const hasBlockquotes = (blockquotes?.length ?? 0) > 0;

    return (
        <section data-testid="pretty-step-one" className="flex flex-col gap-6 lg:flex">
            <header>
                <h1 className="text-base font-bold leading-5 text-content-brand-b md:text-lg md:leading-7 lg:text-center xl:text-left">{title}</h1>
                <h2 className="my-1 text-2xl font-bold leading-8 sm:text-3xl md:text-5xl lg:text-center lg:text-2xl xl:text-left xl:text-3xl">{subTitle}</h2>
            </header>

            {description && <div className="text-sm font-medium text-content-primary sm:text-base md:text-xl">{description}</div>}

            {hasBlockquotes && (
                <div className="flex flex-col gap-3 sm:gap-5 md:gap-6">
                    {blockquoteDescription && <p className="text-base font-medium text-content-primary md:text-xl">{blockquoteDescription}</p>}
                    {blockquotes?.map((q, i) => (
                        <blockquote key={i} className="rounded-lg border px-3 py-2">
                            <div className="text-base font-semibold text-content-primary">{q.topText}</div>
                            <div className="text-sm text-content-secondary">{q.bottomText}</div>
                        </blockquote>
                    ))}
                </div>
            )}

            <div data-testid="policy-whyspot" className="relative flex flex-col gap-2 rounded-xl border border-stroke-secondary-invert px-4 pb-4 pt-6 sm:gap-3 lg:gap-2 xl:gap-3">
                <Badge variant="brandB" className="absolute -top-3 left-1/2 w-fit -translate-x-1/2 px-3 py-1 leading-4">
                    Why choose Spot?
                </Badge>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 md:gap-5 lg:grid-cols-1 lg:gap-3 xl:grid-cols-3 xl:gap-4">
                    {BLURBS.map(b => (
                        <WhySpotBlurb key={b.heading} {...b} />
                    ))}

                    <div data-testid="policy-perks" className="grid grid-cols-4 items-center gap-2 sm:col-span-2 lg:col-span-1 xl:col-span-2">
                        {PERKS.map(({ src, alt }) => (
                            <Image key={alt} src={src} alt={alt} className={PERK_IMAGE_BASE} />
                        ))}
                    </div>
                </div>
            </div>

            <div data-testid="policy-accolades" className={cn("grid w-fit grid-cols-2 items-center gap-3 lg:flex lg:flex-wrap lg:justify-center xl:grid xl:grid-cols-3")}>
                {ACCOLADES.map(({ key, component }) => (
                    <div key={key}>{component}</div>
                ))}
            </div>
        </section>
    );
}
